// ===========================================
// Change Location Dialog (View Lesson page + calendar modal)
// ===========================================
// Move a class to a different studio/room. Mirrors RescheduleClassDialog's
// scope model:
//   - "Just this class" → single occurrence (a RESCHEDULED LessonException that
//      keeps the same date/time but overrides the room via newRoomId)
//   - "All classes"     → recurring room change on the Lesson row (changeRoom)
// Works for all lesson types. A replacement teacher on the affected week(s) is
// shown as CONTEXT but does NOT block the change: who taught and where it was
// held are independent facts. There is no collision to avoid — the two share
// one LessonException row per occurrence, and `rescheduleSingleOccurrence`
// carries `replacementTeacherId` through on both the create and update branches
// (the all-classes path never touches exceptions at all). Blocking here forced
// an admin to destroy a true record to correct a different one.

import { useMemo, useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
  Divider,
  FormControlLabel,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import EventIcon from '@mui/icons-material/Event';
import RepeatIcon from '@mui/icons-material/Repeat';
import PersonOffIcon from '@mui/icons-material/PersonOff';
import HistoryIcon from '@mui/icons-material/History';
import PlaceIcon from '@mui/icons-material/Place';

import WeekTilePicker from '../../booking/WeekTilePicker';
import { useSessionWeeks } from './sessionWeeks';
import LocationRoomPicker from './LocationRoomPicker';
import { Lesson } from '../../../api/lessons.api';
import {
  parseUTCDate,
  currentWeekNumber,
  occurrenceDateForWeek,
  termWeekCount,
  lessonWeekWindow,
} from '../../../utils/dateUtils';
import {
  useChangeLessonRoom,
  useCheckChangeRoomConflicts,
  useRescheduleSingleOccurrence,
  useCheckSingleOccurrenceConflicts,
  useReplacementCoverage,
} from '../../../hooks/useLessons';

const MAX_REASON_LENGTH = 500;

interface ChangeLocationDialogProps {
  open: boolean;
  lesson: Lesson;
  onClose: () => void;
  /** When opened from the calendar (per-occurrence), default to single + this week. */
  defaultMode?: 'single' | 'all';
  defaultWeek?: number;
}

/** Format a Date as a tz-stable YYYY-MM-DD key (UTC components). */
function toDateKey(d: Date): string {
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(
    d.getUTCDate()
  ).padStart(2, '0')}`;
}

export function ChangeLocationDialog({
  open,
  lesson,
  onClose,
  defaultMode = 'all',
  defaultWeek,
}: ChangeLocationDialogProps) {
  const lessonId = lesson.id;
  const termStartDate = lesson.Term?.startDate;
  const termEndDate = lesson.Term?.endDate;
  const enrolledCount = lesson._count?.LessonEnrollment;

  // ----- Local state -----
  const [mode, setMode] = useState<'single' | 'all'>(defaultMode);
  const [sourceWeek, setSourceWeek] = useState<number | undefined>(defaultWeek);
  const [roomId, setRoomId] = useState<string>(lesson.roomId ?? '');
  const [applyToAllTerms, setApplyToAllTerms] = useState(false);
  const [notifyParents, setNotifyParents] = useState(true);
  const [reason, setReason] = useState('');
  const [overrideConflicts, setOverrideConflicts] = useState(false);
  const [roomConflict, setRoomConflict] = useState<string | null>(null);

  // ----- Derived term-week info -----
  const lastWeek = useMemo(() => {
    if (!termStartDate || !termEndDate) return 0;
    return currentWeekNumber(termStartDate, parseUTCDate(termEndDate));
  }, [termStartDate, termEndDate]);

  const currentWeek = useMemo(() => {
    if (!termStartDate) return 0;
    return currentWeekNumber(termStartDate);
  }, [termStartDate]);

  // Weeks affected (drives the informational replacement notice): single → just
  // the chosen week; all → EVERY week (1..lastWeek), because an all-classes ROOM
  // change rewrites Lesson.roomId for the whole row. This no longer gates the
  // submit — it only tells the admin who is covering while they edit.
  // A plain class's own weeks (a fortnightly class, a band's rehearsals) — see the picker below.
  // A HYBRID keeps every tile, as before.
  const lessonSessionWeeks = useSessionWeeks(lesson);
  const plainSessionWeeks = lesson.LessonType?.type === 'HYBRID' ? undefined : (lessonSessionWeeks ?? undefined);

  // The weeks THIS CLASS runs (mid-term start / last week): an all-classes change
  // only concerns those, and the single-class picker offers only those.
  const classWindow = useMemo(
    () =>
      termStartDate && termEndDate
        ? lessonWeekWindow(lesson, termWeekCount(termStartDate, termEndDate))
        : null,
    [lesson, termStartDate, termEndDate]
  );

  const affectedWeeks = useMemo<number[]>(() => {
    if (mode === 'all') {
      const weeks: number[] = [];
      const first = classWindow?.first ?? 1;
      const through = classWindow ? Math.min(lastWeek, classWindow.last) : lastWeek;
      for (let w = first; w <= through; w++) weeks.push(w);
      return weeks;
    }
    return sourceWeek ? [sourceWeek] : [];
  }, [mode, sourceWeek, lastWeek, classWindow]);

  // The occurrence date for the chosen week (single mode) — keys the exception.
  const occurrenceDateKey = useMemo<string | null>(() => {
    if (mode !== 'single' || !sourceWeek || !termStartDate || lesson.dayOfWeek == null) return null;
    // Monday-anchored week grid — matches WeekTilePicker + backend getDateForWeek.
    return toDateKey(occurrenceDateForWeek(parseUTCDate(termStartDate), sourceWeek, lesson.dayOfWeek));
  }, [mode, sourceWeek, lesson.dayOfWeek, termStartDate]);

  /**
   * Is the chosen occurrence already in the past?
   *
   * Drives the whole retroactive affordance: the warning alert, the notify default, and the
   * relaxed conflict rule. Compared at UTC midnight against a UTC-midnight "today", matching
   * the `@db.Date` regime the occurrence date itself comes from — an occurrence dated TODAY
   * is NOT past and behaves exactly as before.
   */
  const isRetroactive = useMemo<boolean>(() => {
    if (mode !== 'single' || !occurrenceDateKey) return false;
    const now = new Date();
    const todayKey = `${now.getUTCFullYear()}-${String(now.getUTCMonth() + 1).padStart(2, '0')}-${String(
      now.getUTCDate()
    ).padStart(2, '0')}`;
    // Both are `YYYY-MM-DD`, so this is a plain lexicographic compare with no timezone frame.
    return occurrenceDateKey < todayKey;
  }, [mode, occurrenceDateKey]);

  // ----- Hooks -----
  const changeRoom = useChangeLessonRoom();
  const rescheduleSingle = useRescheduleSingleOccurrence();
  const checkRoomConflicts = useCheckChangeRoomConflicts();
  const checkSingleConflicts = useCheckSingleOccurrenceConflicts();
  const { data: coverage } = useReplacementCoverage(open ? lessonId : undefined);
  // NOTE: the reinstate mutations were removed with the blocking alert. Reinstating a
  // regular teacher is the replacement dialog's job; this one only changes rooms.

  // ----- Replacement-teacher awareness -----
  const replacementWeekCoverage = useMemo(
    () => (coverage?.weeks ?? []).filter((w) => affectedWeeks.includes(w.weekNumber)),
    [coverage, affectedWeeks]
  );
  const hasGlobalReplacement = !!coverage?.global && affectedWeeks.length > 0;
  const hasReplacementOnAffected = replacementWeekCoverage.length > 0 || hasGlobalReplacement;

  const replacementTeacherName = useMemo(() => {
    if (hasGlobalReplacement && coverage?.global) {
      const t = coverage.global.replacementTeacher;
      return `${t.firstName} ${t.lastName}`;
    }
    if (replacementWeekCoverage.length > 0) {
      const t = replacementWeekCoverage[0].replacementTeacher;
      return `${t.firstName} ${t.lastName}`;
    }
    return '';
  }, [hasGlobalReplacement, coverage, replacementWeekCoverage]);

  // ----- Conflict preview (re-runs when the target room / scope changes) -----
  useEffect(() => {
    setOverrideConflicts(false);
  }, [roomId, mode, applyToAllTerms]);

  useEffect(() => {
    // No check needed when nothing actionable selected or the room is unchanged.
    if (!roomId || roomId === lesson.roomId) {
      setRoomConflict(null);
      return;
    }
    let cancelled = false;

    if (mode === 'all') {
      checkRoomConflicts.mutate(
        { lessonId, params: { roomId, applyToAllTerms } },
        {
          onSuccess: (res) => {
            if (cancelled) return;
            const conflict = res.conflicts.find((c) => c.roomConflict);
            setRoomConflict(
              conflict?.roomConflict
                ? `${conflict.roomConflict.lessonName} on ${conflict.roomConflict.date}`
                : null
            );
          },
          onError: () => !cancelled && setRoomConflict(null),
        }
      );
    } else if (occurrenceDateKey && lesson.startTime && lesson.endTime) {
      checkSingleConflicts.mutate(
        {
          lessonId,
          input: {
            originalDate: occurrenceDateKey,
            newDate: occurrenceDateKey,
            newStartTime: lesson.startTime,
            newEndTime: lesson.endTime,
            newRoomId: roomId,
          },
        },
        {
          onSuccess: (res) => {
            if (cancelled) return;
            setRoomConflict(
              res.roomConflict
                ? `${res.roomConflict.lessonName} on ${res.roomConflict.date}`
                : null
            );
          },
          onError: () => !cancelled && setRoomConflict(null),
        }
      );
    }

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomId, mode, applyToAllTerms, occurrenceDateKey, lessonId, lesson.roomId, lesson.startTime, lesson.endTime]);

  // ----- Reset on (re)open -----
  useEffect(() => {
    if (open) {
      setMode(defaultMode);
      setSourceWeek(defaultWeek);
      setRoomId(lesson.roomId ?? '');
      setApplyToAllTerms(false);
      setNotifyParents(true);
      setReason('');
      setOverrideConflicts(false);
      setRoomConflict(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, lesson.id]);

  /**
   * Follow the retroactive state with the notify default.
   *
   * The week is chosen AFTER the dialog opens, so the reset-on-open above cannot know whether
   * this is a correction. Selecting a past week turns notification off; selecting a future one
   * turns it back on — matching what the server will actually do.
   *
   * This is a convenience, NOT the protection: the server force-suppresses a retroactive room
   * change regardless of what this sends (see `retroactiveRoomChange` in
   * `lessonException.service.ts`). A checkbox is not a security boundary.
   */
  useEffect(() => {
    setNotifyParents(!isRetroactive);
  }, [isRetroactive]);

  const isSubmitting = changeRoom.isPending || rescheduleSingle.isPending;

  const resetAndClose = () => {
    onClose();
  };

  const handleConfirm = () => {
    if (!roomId || roomId === lesson.roomId) return;

    if (mode === 'all') {
      changeRoom.mutate(
        {
          lessonId,
          input: { roomId, applyToAllTerms, notifyParents, reason: reason || undefined, overrideConflicts },
        },
        { onSuccess: resetAndClose }
      );
    } else if (occurrenceDateKey && lesson.startTime && lesson.endTime) {
      // Single occurrence: a RESCHEDULED exception that keeps the date/time but
      // overrides the room.
      rescheduleSingle.mutate(
        {
          lessonId,
          input: {
            originalDate: occurrenceDateKey,
            newDate: occurrenceDateKey,
            newStartTime: lesson.startTime,
            newEndTime: lesson.endTime,
            newRoomId: roomId,
            isRoomOnly: true, // → sends the location-changed email, not reschedule
            notifyParents,
            reason: reason || undefined,
            overrideConflicts,
          },
        },
        { onSuccess: resetAndClose }
      );
    }
  };

  const handleClose = () => {
    if (!isSubmitting) resetAndClose();
  };

  const affectedWeeksLabel =
    affectedWeeks.length === 1
      ? `week ${affectedWeeks[0]}`
      : 'all weeks';

  const confirmDisabled =
    !roomId ||
    roomId === lesson.roomId ||
    (mode === 'single' && !occurrenceDateKey) ||
    // ⚠️ Past weeks only: a conflict on a PAST occurrence is history, not a booking
    // clash, so it informs without blocking. Future weeks still hard-block.
    (!!roomConflict && !overrideConflicts && !isRetroactive) ||

    isSubmitting;

  if (!termStartDate || !termEndDate) {
    // A lesson with no term schedule can't have its location changed per-occurrence.
    return (
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>Change Location</DialogTitle>
        <DialogContent>
          <Alert severity="info">This lesson has no term schedule.</Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Close</Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PlaceIcon color="primary" />
          Change Location — {lesson.name}
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* Scope toggle */}
          <ToggleButtonGroup
            exclusive
            fullWidth
            value={mode}
            onChange={(_e, v) => v && setMode(v)}
            size="small"
          >
            <ToggleButton value="single">
              <EventIcon fontSize="small" sx={{ mr: 1 }} /> Just this class
            </ToggleButton>
            <ToggleButton value="all">
              <RepeatIcon fontSize="small" sx={{ mr: 1 }} /> All classes
            </ToggleButton>
          </ToggleButtonGroup>

          {/* Week picker (single scope only — choose which occurrence) */}
          {mode === 'single' && (
            <Box>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                Which class?
              </Typography>
              <WeekTilePicker
                termStartDate={termStartDate}
                termEndDate={termEndDate}
                selectedWeek={sourceWeek}
                onWeekSelect={setSourceWeek}
                currentWeek={currentWeek}
                occurrenceDayOfWeek={lesson.dayOfWeek ?? undefined}
                // Retroactive room corrections (MNM: "move week 2 from room 1 to room 2" so
                // reporting reflects where the class actually was). This is a SOURCE picker —
                // the week identifies an existing occurrence and the date never moves — so the
                // default `pastRule='occurrence'` is correct and only the past lock is relaxed.
                // Weeks with no session, and weeks outside the term, stay locked regardless.
                allowPast
                // An off week of a plain class (fortnightly, a band's non-rehearsal week) has no
                // session to move to another room — the server refuses it (/qa Phase 4 M1).
                sessionWeeks={plainSessionWeeks}
                windowFirst={classWindow?.first}
                windowLast={classWindow?.last}
              />
            </Box>
          )}

          {/*
            Replacement-teacher NOTICE — informational, never blocking.

            A replacement teacher has no bearing on which room a class is in. The two are
            independent facts about the same occurrence, and the backend already keeps them
            that way: `rescheduleSingleOccurrence` reads the prior exception and writes
            `carriedReplacementTeacherId` on BOTH the create and update branches ("a move must
            never clobber it"), while the all-classes path only updates `Lesson.roomId` and
            never touches exceptions at all.

            This previously demanded the regular teacher be reinstated first — which forced an
            admin to destroy a true record (who actually taught) in order to correct a
            different one (where it was held). That is exactly backwards for a retroactive
            correction, and was wrong for future weeks too.

            Kept as context because it IS useful to see who is covering while you edit.
          */}
          {hasReplacementOnAffected && (
            <Alert severity="info" icon={<PersonOffIcon />}>
              <Typography variant="body2">
                {replacementTeacherName} is covering {affectedWeeksLabel} of this class. Changing
                the location will not affect that.
              </Typography>
            </Alert>
          )}

          {/* Room picker */}
          <Box>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
              New location
            </Typography>
            <LocationRoomPicker
              value={roomId}
              onChange={setRoomId}
              currentRoomId={lesson.roomId}
              enrolledCount={enrolledCount}
              disabled={isSubmitting}
            />
          </Box>

          {/*
            Retroactive notice. Two things the admin must know before saving: this records
            history rather than changing a plan, and NOBODY will be emailed — silence is
            otherwise indistinguishable from a failure.
          */}
          {isRetroactive && (
            <Alert severity="warning" icon={<HistoryIcon />}>
              <Typography variant="subtitle2" gutterBottom>
                This class has already happened
              </Typography>
              <Typography variant="body2">
                This records where the class was actually held — useful when a room change was
                arranged on the day and never entered at the time.{' '}
                <strong>No email will be sent.</strong>
              </Typography>
            </Alert>
          )}

          {/* Room conflict + override */}
          {roomConflict && (
            <Alert severity="warning">
              <Typography variant="body2">
                That room is already in use: {roomConflict}.
              </Typography>
              {isRetroactive ? (
                /*
                  A PAST conflict is information, not an obstacle. Recording where a class
                  actually was is not a booking, and the room genuinely may have been
                  double-booked on the day — that is precisely the history being corrected.
                  So the conflict is shown and the confirm stays enabled.
                  ⚠️ Future weeks keep the hard block below: there, a conflict is a real
                  double-booking that has not happened yet and should be prevented.
                */
                <Typography variant="caption" color="text.secondary">
                  Recorded anyway — this is a past class, so this is what happened rather than
                  a booking.
                </Typography>
              ) : (
                <FormControlLabel
                  sx={{ mt: 1 }}
                  control={
                    <Switch
                      checked={overrideConflicts}
                      onChange={(e) => setOverrideConflicts(e.target.checked)}
                    />
                  }
                  label="Move anyway (override conflict)"
                />
              )}
            </Alert>
          )}

          <Divider />

          {/* All-terms toggle (only meaningful for the recurring scope) */}
          {mode === 'all' && (
            <FormControlLabel
              control={
                <Switch
                  checked={applyToAllTerms}
                  onChange={(e) => setApplyToAllTerms(e.target.checked)}
                />
              }
              label="Also apply to this class in other terms"
            />
          )}

          <FormControlLabel
            control={
              <Switch checked={notifyParents} onChange={(e) => setNotifyParents(e.target.checked)} />
            }
            label="Notify parents by email"
          />

          <TextField
            label="Reason (optional)"
            value={reason}
            onChange={(e) => setReason(e.target.value.slice(0, MAX_REASON_LENGTH))}
            fullWidth
            multiline
            rows={2}
            helperText={`${reason.length}/${MAX_REASON_LENGTH}`}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleConfirm} disabled={confirmDisabled}>
          {isSubmitting ? 'Saving…' : 'Change Location'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default ChangeLocationDialog;
